// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { 
  User as FirebaseUser, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  setPersistence, 
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { User, Lubricentro } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, userData: Partial<User>) => Promise<string>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (userData: Partial<User>) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  // 🆕 Modo Soporte / Impersonación
  impersonatedLubricentro: Lubricentro | null;
  isImpersonating: boolean;
  startImpersonation: (lubricentro: Lubricentro) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [rawUserProfile, setRawUserProfile] = useState<User | null>(null);
  const [impersonatedLubricentro, setImpersonatedLubricentro] = useState<Lubricentro | null>(() => {
    try {
      const saved = localStorage.getItem('hisma_impersonated_lubricentro');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Función para obtener el perfil de usuario desde Firestore
  const fetchUserProfile = async (uid: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'usuarios', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const userData: User = {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLogin: data.lastLogin?.toDate() || null,
          updatedAt: data.updatedAt?.toDate(),
        } as User;
        
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener el perfil de usuario:', error);
      return null;
    }
  };

  // Función para refrescar el perfil del usuario
  const refreshUserProfile = async () => {
    if (currentUser) {
      try {
        await currentUser.reload();
        const profile = await fetchUserProfile(currentUser.uid);
        setRawUserProfile(profile);
        setCurrentUser({ ...currentUser });
      } catch (error) {
        console.error('Error al refrescar perfil de usuario:', error);
      }
    }
  };

  // Observador de cambios en la autenticación
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const userDocRef = doc(db, 'usuarios', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const profile = await fetchUserProfile(user.uid);
            setRawUserProfile(profile);
            
            if (profile) {
              await updateDoc(userDocRef, {
                lastLogin: serverTimestamp()
              });

              if (profile.role === 'admin' && profile.lubricentroId) {
                const lubricentroRef = doc(db, 'lubricentros', profile.lubricentroId);
                await updateDoc(lubricentroRef, {
                  lastLogin: serverTimestamp()
                }).catch(err => console.warn('Error al actualizar lastLogin del lubricentro:', err));
              }
            }
          } else {
            console.warn(`Usuario autenticado pero sin documento en Firestore: ${user.uid}`);
            setRawUserProfile(null);
          }
        } catch (error) {
          console.error('Error al procesar usuario autenticado:', error);
          setRawUserProfile(null);
        }
      } else {
        setRawUserProfile(null);
        setImpersonatedLubricentro(null);
        localStorage.removeItem('hisma_impersonated_lubricentro');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Funciones de Impersonación / Modo Soporte
  const startImpersonation = (lubricentro: Lubricentro) => {
    if (rawUserProfile?.role !== 'superadmin') {
      console.warn('Acceso denegado: solo superadmin puede activar el modo soporte');
      return;
    }
    setImpersonatedLubricentro(lubricentro);
    localStorage.setItem('hisma_impersonated_lubricentro', JSON.stringify(lubricentro));
  };

  const stopImpersonation = () => {
    setImpersonatedLubricentro(null);
    localStorage.removeItem('hisma_impersonated_lubricentro');
  };

  // Perfil efectivo (aplica impersonación transparente si es superadmin)
  const userProfile = useMemo(() => {
    if (!rawUserProfile) return null;
    if (rawUserProfile.role === 'superadmin' && impersonatedLubricentro) {
      return {
        ...rawUserProfile,
        lubricentroId: impersonatedLubricentro.id,
        fantasyName: impersonatedLubricentro.fantasyName || impersonatedLubricentro.responsable
      };
    }
    return rawUserProfile;
  }, [rawUserProfile, impersonatedLubricentro]);


  const isImpersonating = Boolean(rawUserProfile?.role === 'superadmin' && impersonatedLubricentro);

  // Función para iniciar sesión
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para registrar un nuevo usuario
  const register = async (email: string, password: string, userData: Partial<User>): Promise<string> => {
    try {
      setLoading(true);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const newUser = {
        id: user.uid,
        nombre: userData.nombre || '',
        apellido: userData.apellido || '',
        email: user.email || '',
        role: userData.role || 'user',
        estado: userData.role === 'superadmin' ? 'activo' : userData.estado || 'pendiente',
        lubricentroId: userData.lubricentroId || null,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };

      await setDoc(doc(db, 'usuarios', user.uid), newUser);
      
      if (userData.role === 'admin') {
        try {
          await sendEmailVerification(user);
        } catch (emailError) {
          console.warn('Error al enviar email de verificación:', emailError);
        }
      }
      
      const createdUser = {
        ...newUser,
        createdAt: new Date(),
        lastLogin: new Date()
      } as User;
      
      setRawUserProfile(createdUser);
      return user.uid;
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para cerrar sesión
  const logout = async () => {
    try {
      setLoading(true);
      stopImpersonation();
      await signOut(auth);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para restablecer contraseña
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error al enviar correo de restablecimiento:', error);
      throw error;
    }
  };

  // Función para enviar email de verificación
  const sendVerificationEmail = async () => {
    try {
      if (currentUser) {
        await sendEmailVerification(currentUser);
      } else {
        throw new Error('No hay usuario autenticado');
      }
    } catch (error) {
      console.error('Error al enviar email de verificación:', error);
      throw error;
    }
  };

  // Función para actualizar el perfil de usuario
  const updateUserProfile = async (userData: Partial<User>) => {
    try {
      if (!currentUser) throw new Error('No hay usuario autenticado');
      
      await updateDoc(doc(db, 'usuarios', currentUser.uid), {
        ...userData,
        updatedAt: serverTimestamp()
      });
      
      if (rawUserProfile) {
        const updatedProfile = {
          ...rawUserProfile,
          ...userData
        };
        setRawUserProfile(updatedProfile);
      }
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    login,
    register,
    logout,
    resetPassword,
    updateUserProfile,
    sendVerificationEmail,
    refreshUserProfile,
    impersonatedLubricentro,
    isImpersonating,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}